import { getWinType } from "../../utilities";
import { ANIMATED_WIN_TYPES } from "../../featuresConfig";
import { readMysteryFeature } from "./mysteryStaging";
import { WILD_SYMBOL_ID } from "../../SymbolData";
import { SOUND_NAME } from "../../sounds";

/**
 * How much ceremony a mystery reveal deserves.
 *
 * ── Why this can be decided, not guessed ──
 *
 * The reference game has to build tension without knowing the outcome. We do
 * not: the backend resolves the mystery BEFORE it answers, so by the time we
 * present anything we already hold the resolved board, the wins and the
 * amount. Choosing the presentation from the result is not cheating — it is
 * the only honest option, and it can never promise something that does not
 * arrive.
 *
 * Three tiers, and the whole difference between them is WHICH CLIP plays.
 */

/**
 * The build-up clip per tier. The engine reads this as `anticipationClip`.
 *
 * ── One clip, once. No loops. ──
 *
 * This used to be a loop COUNT against a single clip: a board that threatened
 * a big win got three passes of `anticipation`, everything else got none. That
 * was the only lever the rig offered — one short pulse, repeated — and it read
 * as a stutter rather than as tension, which is why the animator replaced it.
 *
 * `my` now carries the two build-ups as authored clips: `anticipation` is the
 * short standard one and `anticipation_strong` is the long dramatic one. Each
 * is a complete performance with its own arc, so each plays ONCE and stops.
 * Repeating either would cut across the shape the animator drew.
 *
 * `none` and `small` share the standard clip. A tier still means what it meant —
 * it just no longer decides how many times to say the same thing.
 */
export const MYSTERY_DRAMA_CLIPS = {
  none: "anticipation",
  small: "anticipation",
  big: "anticipation_strong",
};

/**
 * The build-up SOUND, keyed by the clip rather than by the tier.
 *
 * Keyed that way on purpose. The tier is decided here, but by the time a cell
 * is playing its build-up all it still holds is the clip NAME — it is handed
 * down as `anticipationClip` through two different controllers. Looking the
 * sound up by the clip means the pairing cannot drift: a tier that changes
 * clip takes its sound with it, and a clip with no sound simply stays silent.
 *
 * The lengths match the clips they belong to: 1.5s under the short standard
 * build-up, 3.0s under the long dramatic one.
 */
export const MYSTERY_DRAMA_SOUNDS = {
  anticipation: SOUND_NAME.mystery_reveal_fast,
  anticipation_strong: SOUND_NAME.mystery_reveal_slow,
};

/**
 * @param   {string|null} clip The resolved build-up clip name.
 * @returns {string|null} The sound to play under it, or null for silence.
 */
export const mysteryDramaSound = (clip) =>
  (clip && MYSTERY_DRAMA_SOUNDS[clip]) || null;

/**
 * Cells covered by a mystery AND part of something that paid.
 *
 * Two win shapes, per the BE doc:
 *   ["line",  amount, rowsPerReel, lineId]   rows indexed BY REEL
 *   ["coins", amount, positions]             explicit [reel,row] pairs
 *
 * @param   {object} round
 * @returns {Set<string>} "reel,row" keys
 */
export const mysteryPayingCells = (round) => {
  const feature = readMysteryFeature(round);
  const hit = new Set();
  if (!feature || !Array.isArray(round?.wins)) return hit;

  const isMystery = new Set(
    feature.coords.map(([reel, row]) => `${reel},${row}`),
  );

  for (const win of round.wins) {
    if (!Array.isArray(win)) continue;
    const [kind, , third] = win;

    if (kind === "line" && Array.isArray(third)) {
      // One row per reel, left to right from reel 0.
      third.forEach((row, reel) => {
        const key = `${reel},${row}`;
        if (isMystery.has(key)) hit.add(key);
      });
    } else if (kind === "coins" && Array.isArray(third)) {
      for (const pos of third) {
        if (!Array.isArray(pos)) continue;
        const key = `${pos[0]},${pos[1]}`;
        if (isMystery.has(key)) hit.add(key);
      }
    }
  }

  return hit;
};



/**
 * How much ceremony this board has earned.
 *
 * Scored on POTENTIAL — see mysteryPotential. `ctx` carries the paytable and
 * paylines; without it this falls back to the outcome, which is all a replay
 * or a restored round can offer.
 *
 * @param   {object} round
 * @param   {object} [ctx] {paytable, lines, bet, baseBet}
 * @returns {{tier:"none"|"small"|"big", potential:number, symbolId:number|null}}
 */
export const mysteryDrama = (round, ctx = null) => {
  const feature = readMysteryFeature(round);
  if (!feature) return { tier: "none", potential: 0, symbolId: null };

  if (ctx?.paytable && ctx?.lines) {
    const { win, symbolId } = mysteryPotential(round, ctx);

    // Nothing this board could become pays anything — a mystery on the last
    // reel with no run in front of it. No story to tell.
    if (win <= 0) return { tier: "none", potential: 0, symbolId: null };

    const type = getWinType(win, ctx.bet);
    const tier = ANIMATED_WIN_TYPES.includes(type) ? "big" : "small";
    return { tier, potential: win, symbolId };
  }

  // ── Fallback: judge by what actually happened ──
  if (mysteryPayingCells(round).size === 0) {
    return { tier: "none", potential: 0, symbolId: null };
  }
  const winType = round?.winType || "";
  const tier = ANIMATED_WIN_TYPES.includes(winType) ? "big" : "small";
  return { tier, potential: round?.win ?? 0, symbolId: null };
};

/**
 * @param   {object} round
 * @param   {object} [ctx] {paytable, lines, bet, baseBet}
 * @returns {"none"|"small"|"big"}
 */
export const mysteryDramaTier = (round, ctx = null) => mysteryDrama(round, ctx).tier;

/** The build-up clip for a round. */
export const mysteryAnticipationClip = (round, ctx = null) =>
  MYSTERY_DRAMA_CLIPS[mysteryDramaTier(round, ctx)] ?? MYSTERY_DRAMA_CLIPS.small;

export default mysteryDramaTier;

// ───────────────────────────────────────────────────────────────────────────
// POTENTIAL
// ───────────────────────────────────────────────────────────────────────────

/**
 * The best this board COULD pay if every mystery became the same symbol.
 *
 * ── Why potential and not outcome ──
 *
 * Anticipation is about what the board threatens, not what it delivered. Three
 * mysteries on reels 1-2 in front of a full stack of scientists deserves the
 * long build-up whether or not it lands — that tension is the feature. Scoring
 * the RESULT instead gave the most dramatic boards on screen an instant open
 * whenever they happened to miss, which is backwards.
 *
 * Every mystery reveals the SAME symbol (invariant I2), so the search is one
 * dimension: try each symbol they could become, evaluate the board, keep the
 * best. Roughly 10 symbols x 20 lines of arithmetic.
 *
 * @param   {object} round
 * @param   {object} ctx
 * @param   {object} ctx.paytable  { symbolId: [payout by count-1] }
 * @param   {Array}  ctx.lines     payline table, rows indexed by reel
 * @param   {number} ctx.bet
 * @param   {number} ctx.baseBet
 * @returns {{win:number, symbolId:number|null}} best case, in bet currency
 */
export const mysteryPotential = (round, { paytable, lines, bet, baseBet }) => {
  const feature = readMysteryFeature(round);
  const reels = round?.reels;
  const none = { win: 0, symbolId: null };

  if (!feature || !Array.isArray(reels) || !paytable || !Array.isArray(lines)) {
    return none;
  }

  const scale = baseBet > 0 ? bet / baseBet : 1;
  const mystery = new Set(
    feature.coords.map(([reel, row]) => `${reel},${row}`),
  );

  // Only symbols that actually pay can create potential. B2 is excluded on
  // purpose: it pays nothing by itself, it starts a feature — a different kind
  // of drama that this number cannot express.
  const candidates = Object.keys(paytable)
    .map(Number)
    .filter((id) => Array.isArray(paytable[id]) && paytable[id].some((v) => v));

  let best = none;

  for (const candidate of candidates) {
    let total = 0;

    for (const line of lines) {
      if (!Array.isArray(line)) continue;

      // Walk left to right. A line's symbol is its first non-wild; wilds
      // before it ride along, which is what makes a wild-led line pay as the
      // symbol that follows.
      let symbol = null;
      let count = 0;

      for (let reel = 0; reel < line.length; reel += 1) {
        const row = line[reel];
        const cell = mystery.has(`${reel},${row}`)
          ? candidate
          : reels?.[reel]?.[row];

        if (cell === undefined || cell === null) break;

        const isWild = cell === WILD_SYMBOL_ID;
        if (symbol === null) {
          if (!isWild) symbol = cell;
          count += 1;
          continue;
        }
        if (cell === symbol || isWild) {
          count += 1;
          continue;
        }
        break;
      }

      // An all-wild run pays as the wild itself.
      const paying = symbol ?? WILD_SYMBOL_ID;
      const table = paytable[paying];
      if (!Array.isArray(table) || count < 1) continue;

      const value = table[count - 1] || 0;
      if (value > 0) total += value * scale;
    }

    if (total > best.win) best = { win: total, symbolId: candidate };
  }

  return best;
};
